import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { statusOptions } from '../../mock';
import { Settings as SettingsApi } from '../../lib/api';
import { ChevronLeft, ChevronRight, CalendarClock } from 'lucide-react';

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function daysInMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate(); }

// Pull the date portion (YYYY-MM-DD) out of either a date-only or ISO datetime string.
function dateKeyOf(g) {
  const raw = g.recordingDate || g.episodeDate || '';
  if (!raw) return '';
  return String(raw).slice(0, 10);
}

// Pull the HH:MM portion if a recording time was set, otherwise empty.
function timeOf(g) {
  const raw = g.recordingDate || '';
  if (!raw || raw.length <= 10) return '';
  // Accept "2026-03-14T10:30" or "2026-03-14T10:30:00"
  const t = String(raw).slice(11, 16);
  return /^\d{2}:\d{2}$/.test(t) ? t : '';
}

function colorFor(g) {
  const o = statusOptions.find(s => s.value === g.status);
  return o?.color || '#94A3B8';
}

export default function CalendarView({ onSelect }) {
  const { filteredGuests, activeShow } = useApp();
  const [cursor, setCursor] = useState(new Date());
  const [blocks, setBlocks] = useState({ dates: [], weekly: [] });

  useEffect(() => {
    SettingsApi.get().then(s => setBlocks({ dates: s.blockedDates || [], weekly: s.blockedWeekly || [] })).catch(() => {});
  }, []);

  const fmtWindow = (r) => `${r.startTime || '00:00'}–${r.endTime || '24:00'}`;
  const blockInfoFor = (key, weekdayIdx) => {
    const rules = [
      ...blocks.dates.filter(r => r.date === key),
      ...blocks.weekly.filter(r => Number(r.weekday) === weekdayIdx),
    ];
    if (!rules.length) return null;
    if (rules.some(r => !r.startTime && !r.endTime)) return { wholeDay: true };
    return { wholeDay: false, windows: rules.map(fmtWindow) };
  };

  const first = startOfMonth(cursor);
  const firstWeekday = first.getDay();
  const total = daysInMonth(cursor);
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= total; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLabel = cursor.toLocaleString('en-US', { month: 'long', year: 'numeric' });

  // Group guests by date (recordingDate OR episodeDate). Skip "potential" — they aren't scheduled.
  const guestsByDate = filteredGuests
    .filter(g => g.status !== 'potential')
    .reduce((acc, g) => {
      const k = dateKeyOf(g);
      if (!k) return acc;
      (acc[k] ||= []).push(g);
      return acc;
    }, {});

  // Sort each day's guests by time so 9 AM recordings appear above 3 PM ones.
  Object.values(guestsByDate).forEach(list => list.sort((a, b) => (timeOf(a) || 'zz').localeCompare(timeOf(b) || 'zz')));

  const totalScheduled = Object.values(guestsByDate).reduce((n, list) => n + list.length, 0);

  const fmt = (d) => {
    const m = String(cursor.getMonth() + 1).padStart(2, '0');
    const dd = String(d).padStart(2, '0');
    return `${cursor.getFullYear()}-${m}-${dd}`;
  };

  const todayKey = (() => {
    const t = new Date();
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  })();

  return (
    <div className="p-4 h-full overflow-auto">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h3 className="text-[18px] font-serif text-stone-900">{monthLabel}</h3>
          <span className="inline-flex items-center gap-1 text-[11px] text-stone-500">
            <CalendarClock className="w-3.5 h-3.5" /> {totalScheduled} scheduled this month
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-1.5 hover:bg-stone-100 rounded"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setCursor(new Date())} className="px-2 py-1 hover:bg-stone-100 rounded text-[12px]">Today</button>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-1.5 hover:bg-stone-100 rounded"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 text-[11px] text-stone-500 font-semibold uppercase mb-1">
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d} className="px-2 py-1">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          const key = d ? fmt(d) : '';
          const dayGuests = key ? (guestsByDate[key] || []) : [];
          const isToday = key === todayKey;
          const blk = d ? blockInfoFor(key, i % 7) : null;
          return (
            <div
              key={`cal-${cursor.getFullYear()}-${cursor.getMonth()}-${i}`}
              data-testid={d ? `cal-day-${key}` : undefined}
              className={`min-h-[110px] border rounded p-1.5 ${blk?.wholeDay ? 'bg-stone-100' : 'bg-white'} ${isToday ? 'border-stone-700 ring-1 ring-stone-300' : 'border-stone-200'}`}
            >
              {d && (
                <>
                  <div className={`text-[11px] font-medium mb-1 flex items-center justify-between ${isToday ? 'text-stone-900' : blk?.wholeDay ? 'text-stone-400' : 'text-stone-500'}`}>
                    <span>{d}</span>
                    {blk?.wholeDay && <span data-testid={`cal-blocked-${key}`} className="text-[9px] uppercase tracking-wide text-stone-400 font-semibold">Blocked</span>}
                  </div>
                  {blk && !blk.wholeDay && blk.windows.map((w, wi) => (
                    <div key={wi} data-testid={`cal-blocked-${key}`} className="mb-1 px-1.5 py-0.5 rounded bg-stone-100 text-stone-400 text-[10px] truncate" title={`Blocked ${w}`}>
                      ⊘ {w}
                    </div>
                  ))}
                  <div className="space-y-1">
                    {dayGuests.map(g => {
                      const t = timeOf(g);
                      const bg = colorFor(g);
                      return (
                        <button
                          key={g.id}
                          onClick={() => onSelect(g.id)}
                          title={`${g.status} · ${g.episodeTitle || g.name}${t ? ' · ' + t : ''}`}
                          className="w-full text-left px-1.5 py-1 rounded text-[11px] text-white truncate hover:opacity-90"
                          style={{ background: bg }}
                        >
                          {t && <span className="opacity-80 mr-1">{t}</span>}{g.name}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex items-center gap-3 text-[11px] text-stone-500 flex-wrap">
        <span className="uppercase tracking-wider font-semibold">Legend</span>
        {statusOptions.filter(s => s.value !== 'potential').map(s => (
          <span key={s.value} className="inline-flex items-center gap-1">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />
            {s.label}
          </span>
        ))}
        <span className="ml-auto text-stone-400">Showing for {activeShow.name}</span>
      </div>
    </div>
  );
}
